export type LoopCallback = (dt: number, elapsedTime: number) => void;

export class EngineLoop {
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private timeScale: number = 1.0;
  private callbacks: Set<LoopCallback> = new Set();
  private lastTime: number = 0;
  private elapsedTime: number = 0;
  private animFrameId: number | null = null;

  // Frame timing stats
  public fps: number = 60;
  public frameTimeMs: number = 16.6;
  private frameCount: number = 0;
  private lastFpsUpdate: number = 0;

  constructor() {
    this.lastFpsUpdate = performance.now();

    // Pause when tab is hidden to save battery & prevent leap jumps
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.lastTime = 0; // reset delta on return
      }
    });
  }

  public register(cb: LoopCallback): () => void {
    this.callbacks.add(cb);
    return () => this.callbacks.delete(cb);
  }

  public setPaused(paused: boolean): void {
    this.isPaused = paused;
  }

  public setTimeScale(scale: number): void {
    this.timeScale = Math.max(0, scale);
  }

  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastTime = performance.now();
    this.tick(this.lastTime);
  }

  public stop(): void {
    this.isRunning = false;
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
  }

  private tick = (currentTime: number): void => {
    if (!this.isRunning) return;

    if (this.lastTime === 0) {
      this.lastTime = currentTime;
    }

    // Clamp dt between 0.001 and 0.1s
    const rawDt = (currentTime - this.lastTime) / 1000;
    const clampedDt = Math.min(Math.max(rawDt, 0.001), 0.1);
    this.lastTime = currentTime;

    // Track FPS
    this.frameCount++;
    if (currentTime - this.lastFpsUpdate >= 500) {
      this.fps = Math.round((this.frameCount * 1000) / (currentTime - this.lastFpsUpdate));
      this.frameTimeMs = parseFloat((rawDt * 1000).toFixed(1));
      this.frameCount = 0;
      this.lastFpsUpdate = currentTime;
    }

    const dt = this.isPaused ? 0 : clampedDt * this.timeScale;
    this.elapsedTime += dt;

    for (const cb of this.callbacks) {
      try {
        cb(dt, this.elapsedTime);
      } catch (err) {
        console.error('EngineLoop callback error:', err);
      }
    }

    this.animFrameId = requestAnimationFrame(this.tick);
  };
}
